import { useCallback, useRef } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Button, Flex, Skeleton, Text } from '@nula/core-ui';
import { commands } from '@/lib/platform/generated/bindings.gen';
import { AudioEmptyIllustration } from '@/components/empty-illustrations';
import { isDesktop } from '@/lib/platform';
import { reportCommandError } from '@/lib/recordings/report-command-error';
import { useLiveRecording } from '@/lib/recordings/use-live-recording';
import { useRecordingForSurface } from '@/lib/recordings/use-recording-for-surface';
import { useStartRecording } from '@/lib/recordings/use-start-recording';
import { useRecordingElapsed } from '@/lib/recordings/use-recording-elapsed';
import { IN_FLIGHT_STATUSES, type RecordingStatus } from '@/lib/recordings/types/types';
import { LiveTranscript } from '@/lib/recordings/live-transcript';
import { useHasLiveCaptions } from '@/lib/recordings/use-live-transcript-store';
import { RecordTransport } from '@/lib/recordings/record-transport';
import { TranscriptReader } from '@/lib/recordings/transcript-reader';
import type { SummarizeOffer } from '../hooks/use-template-runs';
import { RecordingStages } from './recording-stages';
import { SurfaceSummarizeRow } from './surface-summarize-row';
import styles from './surface-audio-body.module.css';

interface SurfaceAudioBodyProps {
  /** The document's entity id. Attendees and the recording link both resolve
   *  off this id (ui-design.md §5.1b item 1 — see the report for evidence). */
  entityId: string;
  /** This surface's own manifest id. Every audio surface has one — there is
   *  no legitimate caller without it. Threaded into `startRecording` so the
   *  server can persist which surface asked for the recording (design §3.6). */
  surfaceId: string;
  /** The recording bound to this surface (`SurfaceEntry.recordingId`), or
   *  `null` for a legacy entry written before M1. Resolves this surface's own
   *  recording rather than the document's most recent one (design §3.6). */
  recordingId: string | null;
  /** Whether the viewer may mutate this document. A read-only viewer sees the
   *  surface and its transcript but must not be able to start a recording
   *  (ui-design.md §9.6). */
  canEdit?: boolean;
  /** Offered only by the mount, which withholds it once the meeting has a summary. */
  summarize?: SummarizeOffer;
}

const wrap = cva(styles.wrap);

const PLACEHOLDER_TURNS = [['100%', '72%'], ['100%', '48%'], ['100%', '64%']];

function TranscriptPlaceholder() {
  return (
    <Flex vertical data-testid="transcript-placeholder" className={styles.placeholder}>
      {PLACEHOLDER_TURNS.map((width, i) => (
        <Skeleton
          key={i}
          active={false}
          avatar={{ size: 24 }}
          title={{ width: '20%' }}
          paragraph={{ rows: 2, width }}
        />
      ))}
    </Flex>
  );
}

function pipelineLine(
  status: RecordingStatus | null,
  durationSeconds: number | null,
  hasCaptions: boolean,
): string {
  if (status === null || status === 'uploading') return 'Keep Nula open until the upload finishes.';
  if (hasCaptions) return "The full transcript replaces these live captions when it's ready.";
  const ready = "The transcript appears here when it's ready; you can close Nula.";
  if (durationSeconds === null) return ready;
  return `${Math.max(1, Math.round(durationSeconds / 60))}-minute recording. ${ready}`;
}

interface PipelineStatusProps {
  status: RecordingStatus | null;
  durationSeconds: number | null;
  recordingId: string | null;
}

function PipelineStatus({ status, durationSeconds, recordingId }: PipelineStatusProps) {
  const hasCaptions = useHasLiveCaptions(recordingId);
  return (
    <Flex vertical className={wrap()}>
      <Flex vertical gap="sm" className={styles.pipeline}>
        <RecordingStages status={status} />
        <Text size="sm" type="secondary">{pipelineLine(status, durationSeconds, hasCaptions)}</Text>
        {!hasCaptions && <TranscriptPlaceholder />}
      </Flex>
      {hasCaptions && recordingId && (
        <LiveTranscript className={clsx(styles.transcript, styles.softened)} recordingId={recordingId} />
      )}
    </Flex>
  );
}

/**
 * Renders the recording-produced surface's five states (ui-design.md §5.1b).
 * A reader, not an editor — nothing here writes into the surface's fragment.
 */
export function SurfaceAudioBody({
  entityId, surfaceId, recordingId, canEdit = true, summarize,
}: SurfaceAudioBodyProps) {
  // Scope the live recording to THIS surface. `useLiveRecording` resolves by
  // document, so with two audio surfaces an idle one would otherwise render the
  // other surface's transcript and transport controls.
  const liveForDoc = useLiveRecording(entityId);
  const live = liveForDoc?.surfaceId === surfaceId ? liveForDoc : null;
  const recording = useRecordingForSurface(entityId, surfaceId, recordingId);
  const elapsedMs = useRecordingElapsed(live?.recordingId ?? '');
  const startRecording = useStartRecording();
  const bodyRef = useRef<HTMLElement>(null);
  const focusBody = useCallback(() => bodyRef.current?.focus(), []);

  const handleStart = () => {
    startRecording(entityId, surfaceId);
  };

  if (live?.phase === 'capturing') {
    const handlePause = async () => {
      const res = await commands.recordingPause(live.recordingId);
      if (res.status === 'error') {
        reportCommandError('recording_pause', res.error);
        console.error('recording_pause failed', res.error);
      }
    };
    const handleResume = async () => {
      const res = await commands.recordingResume(live.recordingId);
      if (res.status === 'error') {
        reportCommandError('recording_resume', res.error);
        console.error('recording_resume failed', res.error);
      }
    };
    const handleStop = async () => {
      const res = await commands.recordingStop(live.recordingId, 'user');
      if (res.status === 'error') {
        reportCommandError('recording_stop', res.error);
        console.error('recording_stop failed', res.error);
      }
    };
    return (
      <Flex vertical className={wrap()}>
        <Flex align="center" className={styles.status}>
          <RecordTransport
            entityId={entityId}
            phase={live.phase}
            isPaused={live.isPaused}
            elapsedMs={elapsedMs}
            onPause={handlePause}
            onResume={handleResume}
            onStop={handleStop}
          />
        </Flex>
        <LiveTranscript className={styles.transcript} recordingId={live.recordingId} />
      </Flex>
    );
  }

  if (live?.phase === 'processing') {
    return (
      <PipelineStatus
        status={recording?.status ?? null}
        durationSeconds={recording?.durationSeconds ?? null}
        recordingId={live.recordingId}
      />
    );
  }

  if (recording?.status === 'failed') {
    return (
      <Flex vertical className={styles.failed}>
        <Text size="sm">This recording didn&apos;t finish processing.</Text>
        <Text type="secondary" size="sm">
          The audio was captured but the transcript couldn&apos;t be generated.
        </Text>
        {recording.failureReason && (
          <Text type="secondary" size="sm">{recording.failureReason}</Text>
        )}
      </Flex>
    );
  }

  if (recording && IN_FLIGHT_STATUSES.includes(recording.status)) {
    return (
      <PipelineStatus
        status={recording.status}
        durationSeconds={recording.durationSeconds}
        recordingId={recording.id}
      />
    );
  }

  if (recording?.status === 'complete') {
    const offer = summarize && canEdit ? summarize : null;
    const recordingIdToSummarize = recording.id;
    return (
      <Flex vertical ref={bodyRef} tabIndex={-1} className={wrap()}>
        {offer && (
          <Flex vertical className={styles.summarize}>
            <SurfaceSummarizeRow
              busy={offer.busy}
              refusal={offer.refusal}
              onSummarize={() => offer.onSummarize(recordingIdToSummarize)}
              onHiddenWhileFocused={focusBody}
            />
          </Flex>
        )}
        <TranscriptReader
          recordingId={recording.id}
          entityId={entityId}
          surfaceId={surfaceId}
          className={offer ? clsx(styles.transcript, styles.belowSummarize) : styles.transcript}
        />
      </Flex>
    );
  }

  // The surface itself is creatable and readable on web; only capture needs the
  // Tauri HUD. Saying so beats rendering a button that cannot work — and beats
  // hiding the whole kind on web, which would leave an existing audio surface
  // unopenable there.
  // A play-button-and-waveform mark, matching the app's family of empty-state
  // illustrations rather than a bare line of text.
  // Shaped like the library empty states (trash, documents): a 320px
  // illustration over a glow, a title, then secondary body text.
  return (
    <Flex vertical align="center" justify="center" gap="sm" className={styles.empty}>
      <Flex align="center" justify="center" className={styles.illustrationWrap}>
        <Flex className={styles.illustrationGlow} />
        <AudioEmptyIllustration className={styles.illustration} />
      </Flex>
      <Text className={styles.emptyTitle}>Nothing has been said yet</Text>
      {isDesktop && canEdit ? (
        <>
          <Text className={styles.emptyMessage}>
            Start a recording and every word will land here, transcribed and searchable.
          </Text>
          <Button variant="primary" size="sm" onClick={handleStart}>
            Start recording
          </Button>
        </>
      ) : isDesktop ? (
        <Text className={styles.emptyMessage}>
          Nothing has been recorded on this page yet.
        </Text>
      ) : (
        <Text className={styles.emptyMessage}>
          Capturing audio needs the desktop app. Anything recorded there shows up here,
          transcribed and searchable.
        </Text>
      )}
    </Flex>
  );
}
